import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { PlayerLoyalty } from '@sorare/core/src/atoms/icons/PlayerLoyalty';
import { VaultBoost } from '@sorare/core/src/atoms/icons/VaultBoost';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CARDS_CARDS_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RarityVaultHowItWorksDialogWithFetch } from '#src/components/sealing/RarityVaultHowItWorksDialog/index.withFetch.tsx';
import { SealingGenericAvatar } from '#src/components/sealing/SealingGenericAvatar/index.tsx';
import sealedVaultPlaceholder from '#src/components/sealing/assets/sealed-placeholder.webp';

const Wrapper = styled(Vertical)`
  --sealed-vault-height: 560px;
  --sealed-vault-width: 371px;
  --mask-width: var(--double-unit);

  @media ${tabletAndAbove} {
    --mask-width: 131px;
  }

  width: 100%;
  height: var(--sealed-vault-height);
  margin: var(--double-unit) auto;
  background-image: url(${sealedVaultPlaceholder});
  background-size: var(--sealed-vault-width) var(--sealed-vault-height);
  background-position: center;
  background-repeat: repeat-x;
  position: relative;
  isolation: isolate;
  overflow: hidden;

  opacity: 0;
  animation: ${fadeIn} 1.5s ease-out forwards;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: var(--mask-width);
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(var(--c-rgb-black), 0.99) 30%,
      transparent 100%
    );
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: var(--mask-width);
    height: 100%;
    background: linear-gradient(
      to left,
      rgba(var(--c-rgb-black), 0.99) 30%,
      transparent 100%
    );
    z-index: 1;
  }
`;

const SealedCardSkeleton = styled(Vertical)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: var(--sealed-vault-width);
  height: var(--sealed-vault-height);
  padding: calc(5 * var(--unit));
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: var(--triple-unit);
`;

const StyledSealingGenericAvatar = styled(SealingGenericAvatar)`
  margin-top: var(--quadruple-unit);
`;

const ButtonGroup = styled(Horizontal).attrs({ gap: 2, center: true })`
  flex-wrap: wrap;
  width: 100%;

  opacity: 0;
  animation: ${fadeIn} 0.1s ease-in forwards 1.5s;

  & > * {
    flex: 0 1 40%;
  }
`;

type Props = {
  rarity: Rarity;
};

const subtitleBySport = defineMessages({
  [Sport.BASEBALL]: {
    id: 'Collections.sealing.skeleton.subtitle',
    defaultMessage:
      'Sealing cards increases your Vault and Player Loyalty levels, unlocking perks like boosted gameplay rewards.',
  },
  [Sport.FOOTBALL]: {
    id: 'Collections.sealing.skeleton.football.subtitle',
    defaultMessage:
      'Sealing cards increases your Vault level, unlocking boosted gameplay rewards.',
  },
  [Sport.NBA]: {
    id: 'Collections.sealing.skeleton.football.subtitle',
    defaultMessage:
      'Sealing cards increases your Vault level, unlocking boosted gameplay rewards.',
  },
});

export const EmptyVaultPlaceholderSealed = ({ rarity }: Props) => {
  const [showHowItWorksDialog, setShowHowItWorksDialog] = useState(false);
  const { generateSportContextPath, sport } = useSportContext();

  const cardsTypePath = generateSportContextPath(
    ANY_SPORT_MY_CARDS_CARDS_TYPE,
    {
      params: {
        type: rarity || Rarity.limited,
      },
    }
  );
  return (
    <>
      <RarityVaultHowItWorksDialogWithFetch
        open={showHowItWorksDialog}
        rarity={rarity}
        onClose={() => setShowHowItWorksDialog(false)}
      />
      <Wrapper>
        <SealedCardSkeleton>
          <StyledSealingGenericAvatar />
          <Horizontal gap={4}>
            <VaultBoost size={3} />
            <PlayerLoyalty size={4} />
            <Gift size={2.5} />
          </Horizontal>
          <Vertical gap={4}>
            <Vertical gap={0.5}>
              <HeadlineM>
                <FormattedMessage
                  id="Collections.sealing.skeleton.title"
                  defaultMessage="You don't have any sealed cards"
                />
              </HeadlineM>
              <LabelL color="var(--c-nd-600)">
                <FormattedMessage {...subtitleBySport[sport]} />
              </LabelL>
            </Vertical>
            <ButtonGroup gap={1}>
              <Button
                color="tertiary"
                size="small"
                onClick={() => setShowHowItWorksDialog(true)}
              >
                <FormattedMessage {...glossary.learnMore} />
              </Button>
              <Button
                color="primary"
                size="small"
                to={`${cardsTypePath}?selectionMode=sealing`}
              >
                <FormattedMessage {...fantasy.startSealing} />
              </Button>
            </ButtonGroup>
          </Vertical>
        </SealedCardSkeleton>
      </Wrapper>
    </>
  );
};
